import {
  TronicIncomingWindowMessage,
  TronicOutgoingWindowMessage,
  JsonRpcRequestPayload,
  TronicMessageEvent,
  TronicMessageRequest,
  SDKWarningCode,
} from '@tronic-sdk/types';
import { JsonRpcResponse } from './json-rpc';
import { createPromise } from '../util/promise-tools';
import { getItem, setItem } from '../util/storage';
import { createJwt } from '../util/web-crypto';
import { SDKEnvironment } from './sdk-environment';
import { TronicSDKWarning, createModalNotReadyError } from './sdk-exceptions';

interface RemoveEventListenerFunction {
  (): void;
}

interface StandardizedResponse {
  id?: string | number;
  response?: JsonRpcResponse;
}

interface StandardizedTronicRequest {
  msgType: string;
  payload: JsonRpcRequestPayload<any> | JsonRpcRequestPayload<any>[];
  jwt?: string;
  rt?: string;
}

/**
 * Get the originating payload from a batch request using the specified `id`.
 */
function getRequestPayloadFromBatch(
  requestPayload: JsonRpcRequestPayload | JsonRpcRequestPayload[],
  id?: string | number | null,
): JsonRpcRequestPayload | undefined {
  return id && Array.isArray(requestPayload)
    ? requestPayload.find((p) => p.id === id)
    : (requestPayload as JsonRpcRequestPayload);
}

/**
 * Ensures the incoming response follows the expected schema and parses for a
 * JSON RPC payload ID.
 */
function standardizeResponse(
  requestPayload: JsonRpcRequestPayload | JsonRpcRequestPayload[],
  event: TronicMessageEvent,
): StandardizedResponse {
  const id = event.data.response?.id;
  const requestPayloadResolved = getRequestPayloadFromBatch(requestPayload, id);

  if (id && requestPayloadResolved) {
    // Build a standardized response object
    const response = new JsonRpcResponse(requestPayloadResolved)
      .applyResult(event.data.response.result)
      .applyError(event.data.response.error);

    return { id, response };
  }

  return {};
}

async function createTronicRequest(msgType: string, payload: JsonRpcRequestPayload | JsonRpcRequestPayload[]) {
  const rt = await getItem<string>('rt');
  let jwt;

  // only for webcrypto platforms
  if (SDKEnvironment.platform === 'web') {
    try {
      jwt = (await getItem<string>('jwt')) ?? (await createJwt());
    } catch (e) {
      console.error('webcrypto error', e);
    }
  }

  const request: StandardizedTronicRequest = { msgType, payload };

  if (jwt) {
    request.jwt = jwt;
  }
  if (jwt && rt) {
    request.rt = rt;
  }

  return request;
}

// async function persistTronicEventRefreshToken(event: TronicMessageEvent) {
//   if (!event.data.rt) {
//     return;
//   }

//   await setItem('rt', event.data.rt);
// }

export abstract class ViewController {
  public checkIsReadyForRequest: Promise<void>;
  public isReadyForRequest: boolean;
  protected readonly messageHandlers = new Set<(event: TronicMessageEvent) => any>();
  protected isConnectedToInternet = true;

  /**
   * Create an instance of `ViewController`
   *
   * @param endpoint - The URL for the relevant iframe context.
   * @param parameters - The unique, encoded query parameters for the
   * relevant iframe context.
   * @param networkHash - The hash of the network that this sdk instance is connected to
   * for multi-chain scenarios
   */
  constructor(protected readonly endpoint: string, protected readonly parameters: string) {
    this.checkIsReadyForRequest = this.waitForReady();
    this.isReadyForRequest = false;
    this.listen();
  }

  protected abstract init(): void;
  protected abstract _post(data: TronicMessageRequest): Promise<void>;
  protected abstract hideOverlay(): void;
  protected abstract showOverlay(): void;

  /**
   * Send a payload to the Tronic `<iframe>` for processing and automatically
   * handle the acknowledging follow-up event(s).
   *
   * @param msgType - The type of message to encode with the data.
   * @param payload - The JSON RPC payload to emit via `window.postMessage`.
   */
  public async post<ResultType = any>(
    msgType: TronicOutgoingWindowMessage,
    payload: JsonRpcRequestPayload[],
  ): Promise<JsonRpcResponse<ResultType>[]>;

  public async post<ResultType = any>(
    msgType: TronicOutgoingWindowMessage,
    payload: JsonRpcRequestPayload,
  ): Promise<JsonRpcResponse<ResultType>>;

  public async post<ResultType = any>(
    msgType: TronicOutgoingWindowMessage,
    payload: JsonRpcRequestPayload | JsonRpcRequestPayload[],
  ): Promise<JsonRpcResponse<ResultType> | JsonRpcResponse<ResultType>[]> {
    return createPromise(async (resolve, reject) => {
      if (!this.isConnectedToInternet) {
        const error = createModalNotReadyError();
        reject(error);
      }

      if (!this.isReadyForRequest) {
        await this.waitForReady();
      }

      const batchData: JsonRpcResponse[] = [];
      const batchIds = Array.isArray(payload) ? payload.map((p) => p.id) : [];
      const msg = await createTronicRequest(`${msgType}-${this.parameters}`, payload);

      await this._post(msg);

      /**
       * Collect successful RPC responses and resolve.
       */
      const acknowledgeResponse = (removeEventListener: RemoveEventListenerFunction) => (event: TronicMessageEvent) => {
        const { id, response } = standardizeResponse(payload, event);
        // persistTronicEventRefreshToken(event);

        if (id && response && Array.isArray(payload) && batchIds.includes(id)) {
          batchData.push(response);

          // For batch requests, we wait for all responses before resolving.
          if (batchData.length === payload.length) {
            removeEventListener();
            resolve(batchData);
          }
        } else if (id && response && !Array.isArray(payload) && id === payload.id) {
          removeEventListener();
          resolve(response);
        }
      };

      // Listen for and handle responses.
      const removeResponseListener = this.on(
        TronicIncomingWindowMessage.TRONIC_HANDLE_RESPONSE,
        acknowledgeResponse(() => removeResponseListener()),
      );
    });
  }

  /**
   * Listen for events received with the given `msgType`.
   *
   * @param msgType - The `msgType` encoded with the event data.
   * @param handler - A handler function to execute on each event received.
   * @return A `void` function to remove the attached event.
   */
  public on(
    msgType: TronicIncomingWindowMessage,
    handler: (this: Window, event: TronicMessageEvent) => any,
  ): RemoveEventListenerFunction {
    const boundHandler = handler.bind(window);

    // We cannot effectively cover this function because it never gets reference
    // by value. The functionality of this callback is tested within
    // `initMessageListener`.
    /* istanbul ignore next */
    const listener = (event: TronicMessageEvent) => {
      if (event.data.msgType === `${msgType}-${this.parameters}`) boundHandler(event);
    };

    this.messageHandlers.add(listener);
    return () => this.messageHandlers.delete(listener);
  }

  private waitForReady() {
    return new Promise<void>((resolve) => {
      const unsubscribe = this.on(TronicIncomingWindowMessage.TRONIC_OVERLAY_READY, () => {
        this.isReadyForRequest = true;
        resolve();
        unsubscribe();
      });
    });
  }

  /**
   * Listen for messages sent from the underlying Tronic `<WebView>`.
   */
  private listen() {
    this.on(TronicIncomingWindowMessage.TRONIC_HIDE_OVERLAY, () => {
      this.hideOverlay();
    });

    this.on(TronicIncomingWindowMessage.TRONIC_SHOW_OVERLAY, () => {
      this.showOverlay();
    });

    this.on(TronicIncomingWindowMessage.TRONIC_SEND_PRODUCT_ANNOUNCEMENT, (event: TronicMessageEvent) => {
      if (event.data.response.result.product_announcement) {
        new TronicSDKWarning(SDKWarningCode.ProductAnnouncement, event.data.response.result.product_announcement).log();
      }
    });
  }
}
